import React from "react";
import styles from "./InsightsHighlights.module.css";
import Image from "next/image";
import Link from "next/link";
import { formatDate } from "../../utils/utils";
import { ArrowRight } from "lucide-react";
import { handleLinkClick } from "@/Config/helper";
import { useRouter } from "next/router";

interface InsightImage {
  id: number;
  documentId: string;
  url: string;
  alternativeText: string;
}

interface InsightCard {
  id: number;
  title: string;
  date: string;
  reading_time: string;
  category: string;
  image: InsightImage[];
  url: string;
}

interface MoreInsightsLink {
  id: number;
  label: string;
  url: string;
}

interface InsightsData {
  id: number;
  title: string;
  insight_cards: InsightCard[];
  more_insights_link: MoreInsightsLink;
}

interface Props {
  data: InsightsData;
}

const InsightsHighlights: React.FC<Props> = ({ data }) => {
  const router = useRouter();
  const insightsData = data?.insight_cards || [];

  return (
    <section
      className={styles.insightsSection}
      data-section="InsightsHighlights"
    >
      <div className={styles.insightsContainer}>
        <div className="container">
          <div className={styles.header}>
            <h2 className={styles.heading}>{data?.title}</h2>
            {data?.more_insights_link?.url && (
              <div className="d-none d-md-block">
                <Link
                  href={data?.more_insights_link?.url}
                  title={data?.more_insights_link?.label}
                  className={styles.viewAll}
                  onClick={(e) =>
                    handleLinkClick(e, data?.more_insights_link?.url, router)
                  }
                  shallow
                  passHref
                >
                  <button>
                    {data?.more_insights_link?.label}
                    <ArrowRight />
                  </button>
                </Link>
              </div>
            )}
          </div>

          <div className={styles.insightsRow}>
            {insightsData?.length > 0 && (
              <div key={insightsData[0]?.id} className={styles.largeCard}>
                <Link
                  href={insightsData[0]?.url || ""}
                  target="_blank"
                  title={insightsData[0]?.title}
                  aria-label={insightsData[0]?.title || "blog"}
                  shallow
                  passHref
                >
                  <div className={styles.overlay}>
                    <div className={styles.overlayContent}>
                      <p className={styles.meta}>
                        {formatDate(insightsData[0]?.date)} •
                        {insightsData[0]?.reading_time}
                      </p>
                      <h3 className={styles.title}>{insightsData[0]?.title}</h3>
                    </div>
                  </div>
                  <Image
                    src={insightsData[0]?.image[0]?.url}
                    alt={insightsData[0]?.image[0]?.alternativeText || "Blog"}
                    title={insightsData[0]?.image[0]?.alternativeText || "Blog"}
                    className={styles.image}
                    width={500}
                    height={500}
                    loading="lazy"
                  />
                </Link>
              </div>
            )}

            {insightsData?.slice(1)?.map((item) => (
              <div key={item.id} className={styles.insightCard}>
                <Link
                  href={item.url || ""}
                  title={item?.image[0]?.alternativeText || "Blog"}
                  target="_blank"
                  aria-label={item.title || "blog"}
                  shallow
                  passHref
                >
                  <Image
                    src={item?.image[0]?.url}
                    alt={item?.image[0]?.alternativeText || "Blog"}
                    title={item?.image[0]?.alternativeText || "Blog"}
                    className={styles.image}
                    width={500}
                    height={500}
                    loading="lazy"
                  />
                </Link>
                <div className={styles.content}>
                  <p className={styles.category}>{item?.category}</p>
                  <Link
                    href={item?.url || ""}
                    className={styles.titleLink}
                    target="_blank"
                    aria-label={item?.title || "blog"}
                    title={item?.title}
                    shallow
                    passHref
                  >
                    <h3 className={styles.title}>{item?.title}</h3>
                  </Link>
                  <span className={styles.meta}>
                    {formatDate(item?.date)} • {item?.reading_time}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="d-block d-md-none">
            <div className={styles.moreInsightsWrapper}>
              <Link
                href={data?.more_insights_link?.url}
                className={styles.viewAllMobile}
                title={data?.more_insights_link?.label}
                onClick={(e) =>
                  handleLinkClick(e, data?.more_insights_link?.url, router)
                }
                passHref
                shallow
              >
                <button>
                  {data?.more_insights_link?.label}
                  <ArrowRight />
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InsightsHighlights;
